// ============================================================
// RetourAtelierPage — le bureau tranche, le plan sous les yeux.
// ------------------------------------------------------------
// C'est la couche 4 de l'apprentissage : **la seule vérité terrain.**
// Tout le reste n'apprend que des estimations du deviseur ; ici on
// compare le temps DEVISÉ au temps RÉELLEMENT PASSÉ.
//
// ── La disposition est celle de la vue plan, et c'est voulu ──
//
// À gauche le dessin de la pièce, à droite les temps saisis à l'atelier.
// Charles ne peut pas juger « 45 minutes de montage » dans l'abstrait :
// il juge en regardant la pièce. Séparer les deux obligerait à ouvrir un
// autre écran pour vérifier, donc à ne pas vérifier.
//
// ── « La pièce arrive toujours dans à vérifier » ──
//
// Aucun retour n'entre dans les moyennes sans un geste humain. Un
// chiffre tapé de travers à la machine fausserait l'écart devisé/réel
// sans que personne le voie — et c'est précisément la mesure qu'on
// construit. **Seules les pièces validées comptent pour la mémoire.**
//
// Un retour ÉCARTÉ n'est pas supprimé : il dit qu'on a regardé et décidé
// de ne pas en tenir compte, ce qui n'est pas la même chose que
// l'absence de retour.
//
// ── Deux provenances, une seule vue ──
//
// Le QR et la photo arrivent au même endroit, avec une étiquette
// différente. Un retour n'est pas moins vrai parce qu'il est arrivé par
// une photo — il demande seulement d'avoir été lu correctement, ce que
// cet écran permet justement de vérifier.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Table from '../../ui/Table.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { totauxAffichables } from '@shared/gamme.js';
import { formatMesure, formatDateTime } from '../../lib/format.js';
import PlanViewer from '../devis/PlanViewer.jsx';
import ChampAuto from './ChampAuto.jsx';
import PhotosRecues from './PhotosRecues.jsx';
import BilanCommande from './BilanCommande.jsx';
import './atelier.css';

const ONGLETS = [
  { statut: 'draft', label: 'À vérifier' },
  { statut: 'validated', label: 'Validés' },
  { statut: 'ignored', label: 'Écartés' },
];

/** D'où vient le retour. Deux chemins, deux étiquettes, une seule vue. */
const SOURCES = {
  form: { tone: 'ok', label: 'QR' },
  photo: { tone: 'neutral', label: 'Télégramme' },
  manual: { tone: 'neutral', label: 'Saisi au bureau' },
};

/** L'écart entre prévu et réel, en clair. C'est LE chiffre de l'écran. */
function Ecart({ prevu, reel }) {
  if (prevu == null || reel == null) return <span className="field__hint">—</span>;
  const d = reel - prevu;
  if (!prevu) return <span className="field__hint">{formatMesure(reel, 'min')}</span>;
  const pct = Math.round((d / prevu) * 100);
  // Au-delà de 30 % on le signale : c'est le seuil à partir duquel
  // l'estimation ne s'est pas seulement trompée, elle s'est trompée de
  // façon qui vaut la peine d'être comprise.
  return (
    <Badge tone={Math.abs(pct) > 30 ? 'warn' : 'neutral'}>
      {d > 0 ? '+' : ''}{pct} %
    </Badge>
  );
}

export function RetourAtelierPage() {
  const toast = useToast();
  const [statut, setStatut] = useState('draft');
  const [ouvert, setOuvert] = useState(null);
  const [detail, setDetail] = useState(null);
  // La pièce ouverte, lue par le sondage sans le faire redémarrer à
  // chaque ouverture : une `ref` ne fait pas partie des dépendances.
  const ouvertRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(() => api.get(`/atelier/retours?statut=${statut}`), [statut]);
  const { data, loading, error, reload } = useResource(charger, [statut]);

  /**
   * L'écran se met à jour TOUT SEUL — c'est ce que « en simultané »
   * veut dire.
   *
   * Un opérateur saisit à la machine pendant que Charles regarde son
   * écran au bureau : si la page ne bouge pas, il croit que rien n'est
   * arrivé et va relancer l'atelier pour rien. Une saisie qui n'apparaît
   * qu'après un rafraîchissement manuel est une saisie qu'on croit
   * perdue.
   *
   * Dix secondes : l'atelier ne tape pas trois valeurs par seconde, et
   * une requête de liste toutes les dix secondes ne coûte rien. Le
   * détail ouvert suit le même rythme — c'est LUI qu'on regarde quand on
   * attend un retour.
   *
   * Le sondage s'arrête dès qu'on quitte « à vérifier » : les dossiers
   * validés et écartés ne bougent plus tout seuls.
   */
  useEffect(() => {
    if (statut !== 'draft') return undefined;
    const t = setInterval(() => {
      reload();
      if (ouvertRef.current) {
        api.get(`/atelier/retours/${ouvertRef.current}`).then(setDetail).catch(() => {});
      }
    }, 10000);
    return () => clearInterval(t);
  }, [statut, reload]);

  async function ouvrir(r) {
    setOuvert(r);
    ouvertRef.current = r.piece_id;
    setDetail(null);
    try {
      setDetail(await api.get(`/atelier/retours/${r.piece_id}`));
    } catch (err) {
      toast.error(err.message);
    }
  }

  /**
   * SAISIR OU CORRIGER LE RÉEL DEPUIS LE BUREAU.
   *
   * Sans ce chemin, un « 226 » tapé pour « 26 » ne laisse que deux
   * issues : valider une valeur fausse, ou écarter un retour par
   * ailleurs bon. Dans les deux cas la mesure est perdue — et c'est la
   * seule vérité terrain du programme.
   *
   * C'est aussi la voie de la feuille revenue AU CRAYON : tant que le
   * bot Telegram n'existe pas, Charles la lit et la tape ici.
   *
   * La colonne n'est ouverte qu'en « à vérifier ». Un retour tranché ne
   * se retouche plus : il a servi à corriger le devis, le rouvrir en
   * silence rendrait la correction inexplicable.
   */
  async function ecrire(gammeId, champ, valeur) {
    await api.post(`/atelier/retours/${ouvert.piece_id}/valeur`, {
      gamme_id: gammeId, champ, valeur,
    });
    // On RELIT : les trois totaux se dérivent côté serveur par la même
    // fonction que les temps prévus, et c'est elle qui fait foi.
    setDetail(await api.get(`/atelier/retours/${ouvert.piece_id}`));
    reload();
  }

  async function trancher(decision) {
    if (!ouvert) return;
    setBusy(true);
    try {
      await api.post(`/atelier/retours/${ouvert.piece_id}/decision`, { statut: decision });
      toast.success(
        decision === 'validated'
          ? 'Retour validé — il compte désormais dans la mémoire du programme.'
          : 'Retour écarté — il reste consultable, il n’entre dans aucune moyenne.',
      );
      setOuvert(null);
      ouvertRef.current = null;
      setDetail(null);
      await reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  const colonnes = [
    { key: 'ref_interne', label: 'N°', render: (r) => <span className="num">{r.ref_interne}</span> },
    { key: 'name', label: 'Pièce', render: (r) => r.name || r.ref || '—' },
    { key: 'devis_reference', label: 'Devis' },
    { key: 'client', label: 'Client', render: (r) => r.client || '—' },
    { key: 'operator', label: 'Opérateur', render: (r) => r.operator || '—' },
    {
      // ── Une feuille IMPRIMÉE n'est pas une feuille REMPLIE ──
      //
      // Le retour s'ouvre dès l'impression : sans cette colonne, une
      // feuille partie à l'atelier et jamais revenue se lirait comme un
      // retour à zéro minute.
      key: 'lignes_saisies',
      label: 'Saisie',
      render: (r) => (r.lignes_saisies
        ? <span className="num">{r.lignes_saisies} op.</span>
        : <Badge tone="neutral">en attente</Badge>),
    },
    {
      key: 'source',
      label: 'Par',
      render: (r) => {
        const s = SOURCES[r.source] ?? SOURCES.manual;
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    { key: 'derniere_saisie', label: 'Dernière saisie', render: (r) => formatDateTime(r.derniere_saisie) },
    {
      key: 'ouvrir',
      label: '',
      render: (r) => (
        <Button size="sm" variant="secondary" onClick={() => ouvrir(r)}>
          Examiner
        </Button>
      ),
    },
  ];

  return (
    <div className="stack">
      <Card>
        <CardHead title="Retour atelier">
          {ONGLETS.map((o) => (
            <Button
              key={o.statut}
              size="sm"
              variant={statut === o.statut ? 'primary' : 'ghost'}
              onClick={() => {
                setStatut(o.statut);
                setOuvert(null);
                ouvertRef.current = null;
              }}
            >
              {o.label}
            </Button>
          ))}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Le temps réellement passé à l’atelier, remonté par le QR de la feuille ou par une
            photo. C’est la <strong>seule vérité terrain</strong> : tout le reste n’apprend que des
            estimations du deviseur. Une pièce arrive toujours <strong>à vérifier</strong> —{' '}
            <strong>seules les pièces validées comptent pour la mémoire</strong>.
          </p>

          {loading ? <SkeletonRows rows={4} /> : null}
          {error ? <ErrorState error={error} onRetry={reload} /> : null}
          {!loading && !error ? (
            <Table
              columns={colonnes}
              rows={data.retours}
              caption={`Retours — ${ONGLETS.find((o) => o.statut === statut).label}`}
              getRowKey={(r) => r.piece_id}
              empty={
                <EmptyState title="Aucun retour ici">
                  {statut === 'draft'
                    ? 'Imprimez une feuille de production depuis un devis : le QR ouvre le formulaire, et la saisie arrive ici.'
                    : 'Rien n’a encore été classé dans ce dossier.'}
                </EmptyState>
              }
            />
          ) : null}
        </CardBody>
      </Card>

      {/* La boîte de réception ne s'affiche que sur « à vérifier » : une
          photo non rattachée attend une main, elle n'a rien à faire dans
          les dossiers déjà classés. */}
      {statut === 'draft' ? <PhotosRecues onRattache={reload} /> : null}

      {/* ── LE PLAN À GAUCHE, LES TEMPS À DROITE ──
          On ne juge pas « 45 minutes de montage » dans l'abstrait : on
          juge en regardant la pièce. */}
      {ouvert ? (
        <Card>
          <CardHead title={`${ouvert.ref_interne} — ${ouvert.name || ouvert.ref || 'Pièce'}`}>
            <Badge tone={(SOURCES[ouvert.source] ?? SOURCES.manual).tone}>
              {(SOURCES[ouvert.source] ?? SOURCES.manual).label}
            </Badge>
            {statut === 'draft' ? (
              <>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => trancher('ignored')}>
                  Ne pas en tenir compte
                </Button>
                <Button
                  size="sm" variant="primary"
                  disabled={busy || !ouvert.lignes_saisies}
                  title={ouvert.lignes_saisies ? undefined : 'Aucun temps saisi : il n’y a rien à valider.'}
                  onClick={() => trancher('validated')}
                >
                  Valider ces temps
                </Button>
              </>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => { setOuvert(null); ouvertRef.current = null; }}>Fermer</Button>
          </CardHead>
          <CardBody>
            <div className="plan-et-fiche plan-et-fiche--retour">
              {ouvert.plan_file_id ? (
                <PlanViewer
                  devisId={ouvert.devis_id}
                  pages={[{ id: ouvert.plan_file_id, page_number: null }]}
                  pageId={ouvert.plan_file_id}
                  reference={ouvert.ref}
                  onSelectPage={() => {}}
                />
              ) : (
                <p className="field__hint">
                  Cette pièce n’a pas de plan rattaché — elle a été saisie à la main, ou son devis
                  est une copie.
                </p>
              )}

              <div className="stack">
                {!detail ? <SkeletonRows rows={3} /> : (
                  <>
                    <Table
                      caption="Prévu contre réel, opération par opération"
                      columns={[
                        { key: 'op_num', label: 'OP', numeric: true },
                        { key: 'libelle', label: 'Opération' },
                        {
                          key: 'temps_min',
                          label: 'Prévu',
                          numeric: true,
                          render: (l) => formatMesure(l.temps_min, 'min'),
                        },
                        {
                          key: 'temps_reel_min',
                          label: 'Réel',
                          numeric: true,
                          render: (l) => (statut === 'draft' ? (
                            <ChampAuto
                              nu
                              label={`Temps réel — OP ${l.op_num} ${l.libelle}`}
                              valeur={l.temps_reel_min}
                              unite="min"
                              onEnregistrer={(v) => ecrire(l.id, 'temps_reel_min', v)}
                            />
                          ) : formatMesure(l.temps_reel_min, 'min')),
                        },
                        {
                          key: 'ecart',
                          label: 'Écart',
                          render: (l) => <Ecart prevu={l.temps_min} reel={l.temps_reel_min} />,
                        },
                      ]}
                      rows={detail.lignes}
                      getRowKey={(l) => l.id}
                    />

                    {/* Les totaux viennent d'UNE fonction partagée (`totauxAffichables`) :
                        un `if` de plus ici afficherait « 0 min » le jour où le modèle change. */}
                    <Table
                      caption="Les temps, dérivés des deux côtés par la même fonction"
                      columns={[
                        { key: 'quoi', label: '' },
                        { key: 'prevu', label: 'Devisé', numeric: true },
                        { key: 'reel', label: 'Réel', numeric: true },
                        { key: 'ecart', label: 'Écart' },
                      ]}
                      rows={totauxAffichables(detail.prevu).map((t) => {
                        const reel = totauxAffichables(detail.reel).find((r) => r.cle === t.cle);
                        return {
                          quoi: `${t.label} (${t.portee})`,
                          prevu: formatMesure(t.minutes, 'min'),
                          reel: formatMesure(reel?.minutes ?? null, 'min'),
                          ecart: <Ecart prevu={t.minutes} reel={reel?.minutes ?? null} />,
                        };
                      })}
                      getRowKey={(r) => r.quoi}
                    />
                    <BilanCommande bilan={detail.bilan} />

                    {statut === 'draft' ? (
                      <div className="atelier__entete-bureau">
                        <ChampAuto
                          label="Opérateur" type="text" valeur={detail.entete.operator}
                          onEnregistrer={(v) => ecrire(null, 'operator', v)}
                        />
                        <ChampAuto
                          label="Pièces bonnes" valeur={detail.entete.produced_qty}
                          sousTitre={`devisé pour ${ouvert.qty}`}
                          onEnregistrer={(v) => ecrire(null, 'produced_qty', v)}
                        />
                        <ChampAuto
                          label="Rebuts" valeur={detail.entete.scrap_qty}
                          onEnregistrer={(v) => ecrire(null, 'scrap_qty', v)}
                        />
                      </div>
                    ) : (
                      <p className="field__hint">
                        {detail.entete.operator ? `Saisi par ${detail.entete.operator}. ` : ''}
                        {detail.entete.produced_qty != null
                          ? `${detail.entete.produced_qty} pièce(s) bonne(s). ` : ''}
                        {detail.entete.scrap_qty ? `${detail.entete.scrap_qty} rebut(s). ` : ''}
                        Devisé pour {ouvert.qty} pièce(s).
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

export default RetourAtelierPage;
